import axios from "axios";
import type { ActionContext } from "vuex";
import type { State } from "../VuexStore";
import { Calibration, UserCalibrationMetadata } from "../classes/calibration";
import {
  calibrationMatchesCurrentDevice,
  clampedColourCorrection,
} from "./calibration_core";
import { calibrationSync } from "./calibration_sync";

// Serialize this tab's saves so successive wizard steps reuse a newly created record.
let calibration_save_queue: Promise<unknown> = Promise.resolve();

export function updateCurrentCalibration(
  { commit, state }: ActionContext<State, State>, calibration: Calibration,
): Promise<boolean> {
  const user = state.current_user;
  if (!user) return Promise.resolve(false);
  const sync = calibrationSync(state.app_config.id);
  const message = sync.create(user.id!, calibration);
  const snapshot = Calibration.fromJson(message.calibration);
  // Last line of defence before the wire: a brightness outside the supported
  // range makes the stored record unreadable, so it would be saved and then
  // refused on the way back in.
  snapshot.colour_correction = clampedColourCorrection(snapshot.colour_correction);
  const save = async () => {
    if (state.current_user !== user) return false;
    // A previous queued step may have created this screen's record.
    const current = state.current_calibration;
    if (!snapshot.id && current?.user_id === user.id && calibrationMatchesCurrentDevice(current, snapshot)) {
      snapshot.id = current.id;
      snapshot.user_id = current.user_id;
    }
    const do_update = Boolean(snapshot.id && snapshot.user_id === user.id);
    snapshot.user_id = user.id;
    if (!do_update) snapshot.id = null;
    const metadata = UserCalibrationMetadata.fromJson({
      id: snapshot.id, user_id: user.id, clinic_id: null,
      category: "calibration", data: snapshot,
    });
    try {
      const response = do_update
        ? await axios.put("/api/user_metadata/" + snapshot.id, metadata.toJsonObj())
        : await axios.post("/api/user_metadata", metadata.toJsonObj());
      if (state.current_user !== user) return false;
      if (response.data.status !== "success") {
        commit("setErrorMessage", "Error saving calibrations: " + response.data.error_message);
        return false;
      }
      if (!do_update) snapshot.id = response.data.data;
      message.calibration = snapshot;
      if (sync.accept(message)) {
        // A save for a screen we have left must not replace the current screen.
        commit("applySavedCalibration", snapshot);
        sync.publish(message);
      }
      return true;
    } catch (error) {
      if (state.current_user === user) {
        commit("setErrorMessage", "Error saving calibrations: " + error);
      }
      return false;
    }
  };
  const result = calibration_save_queue.then(save);
  calibration_save_queue = result;
  return result;
}
